import React, { useCallback, useEffect, useState } from 'react';
import {
    Anchor,
    Button,
    Center,
    Container,
    Image,
    Notification,
    Paper,
    PasswordInput,
    Stack,
    Text,
    TextInput,
    Title,
} from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import appmedLogo from '../../../assets/appMed.png';
import { t } from 'i18next';
import { IconAt, IconLock } from '@tabler/icons-react';
import * as z from 'zod';
import { inject, observer } from 'mobx-react';
import { useGetPatientInvitationsByToken } from '../../../global/entities/PatientInvitations/queries/useGetPatientInvitationsByToken.tsx';
import { usePostPatients } from '../../../global/entities/Patients/mutations/usePostPatientsMutations.tsx';
import { usePostLoginMutation } from '../../../global/entities/Login/mutations/usePostLoginMutation.tsx';
import { AppMedError } from '../../../global/error/AppMedError.ts';
import { useDisclosure } from '@mantine/hooks';
import TermsModal from '../../../global/component/organism/TermsModal/TermsModal.tsx';
import PrivacyModal from '../../../global/component/organism/PrivacyModal/PrivacyModal.tsx';
import Shell from '../../../global/component/organism/Shell/Shell.tsx';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PostHogService } from '../../../global/services/posthogService.ts';

const RegisterForm: React.FC = inject()(
    observer(() => {
        const [error, setError] = useState<string | null>(null);
        const [isSubmitting, setIsSubmitting] = useState(false);
        const [countdown, setCountdown] = useState(15);
        const urlParams = new URLSearchParams(window.location.search);
        const isExpired = urlParams.get('expired') === 'true';
        const token = urlParams.get('token') ?? '';
        const navigate = useNavigate();
        const { data: patientInvitationData, isError, isFetched } = useGetPatientInvitationsByToken(token, !isExpired);
        const usePostPatientsMutation = usePostPatients();
        const addCurrentUserMutation = usePostLoginMutation();
        const { i18n } = useTranslation();

        const isInvalidToken = isExpired || (isFetched && isError);

        const goToLogin = useCallback(() => {
            navigate('/login');
        }, [navigate]);

        useEffect(() => {
            if (!isInvalidToken) return;

            if (countdown <= 0) {
                goToLogin();
                return;
            }

            const timer = setTimeout(() => {
                setCountdown((prev) => prev - 1);
            }, 1000);

            return () => clearTimeout(timer);
        }, [isInvalidToken, countdown, goToLogin]);

        useEffect(() => {
            if (!token) return;

            if (isInvalidToken) {
                PostHogService.capture('invitation link expired', {
                    token_present: !!token,
                    expired_param: isExpired,
                });
            }
        }, [isInvalidToken, token, isExpired]);

        useEffect(() => {
            if (!token || isExpired) return;

            PostHogService.capture('invitation link opened', {
                has_token: true,
            });
        }, [token, isExpired]);

        const [openedTerms, { open: openTerms, close: closeTerms }] = useDisclosure(false);
        const [openedPrivacy, { open: openPrivacy, close: closePrivacy }] = useDisclosure(false);

        const schema = z
            .object({
                firstName: z.string().min(1, { message: t('formError.firstName') }),
                lastName: z.string().min(1, { message: t('formError.lastName') }),
                email: z.string().email({ message: t('formError.invalidEmailAddress') }),
                password: z
                    .string()
                    .min(8, { message: t('formError.passwordMustBeAtLeast8CharactersLong') })
                    .regex(/[a-z]/, { message: t('formError.passwordMustContainLowercase') })
                    .regex(/[A-Z]/, { message: t('formError.passwordMustContainUppercase') })
                    .regex(/[0-9]/, { message: t('formError.passwordMustContainDigit') })
                    .regex(/[#!?@$%^&*\-_]/, { message: t('formError.passwordMustContainSpecialCharacter') }),
                confirmPassword: z.string().min(1, { message: t('formError.confirmPasswordIsRequired') }),
            })
            .refine((data) => data.password === data.confirmPassword, {
                message: t('formError.passwordsMustMatch'),
                path: ['confirmPassword'],
            });

        const form = useForm({
            initialValues: {
                firstName: '',
                lastName: '',
                password: '',
                confirmPassword: '',
                email: '',
            },
            validate: zodResolver(schema),
        });

        const handleSubmit = async (values: typeof form.values) => {
            if (!urlParams.get('token'))
                return;

            PostHogService.capture('registration form submitted', { language: i18n.language });
            setError(null);
            setIsSubmitting(true);

            try {
                await usePostPatientsMutation
                    .mutateAsync({
                        email: values.email,
                        firstName: values.firstName,
                        lastName: values.lastName,
                        password: values.password,
                        invitationToken: urlParams.get('token')!,
                    })
                    .then(() => {
                        PostHogService.capture('user registered', { language: i18n.language });
                        return login({ email: values.email, password: values.password });
                    });
            } catch (e) {
                PostHogService.capture('registration form error', { error: e instanceof AppMedError ? e.message : 'unexpected_error' });
                setError(e instanceof AppMedError && e.message ? e.message : t('formRegister.registrationFailed'));
            } finally {
                setIsSubmitting(false);
            }
        };

        const login = async (login: { email: string; password: string }) => {
            try {
                const loginRep = await addCurrentUserMutation.mutateAsync({
                    username: login.email,
                    password: login.password,
                });
                window.location.href = loginRep.redirect;
            } catch {
                setError(t('formRegister.signInAfterRegistrationFailed'));
            }
        };

        useEffect(() => {
            if (!patientInvitationData) 
                return;
            
            form.setValues(patientInvitationData);
        }, [patientInvitationData]);

        const isAnonymizedMode = patientInvitationData?.isAnonymizedMode || false;

        if (isInvalidToken) {
            return (
                <Shell>
                    <Container mt={0} mb={0} size={'default'} my={40} bg={'var(--mantine-color-gray-1)'} mih={'100vh'}>
                        <Stack align={'center'} w={'100%'} h={'auto'} gap={'sm'} justify={'flex-start'}>
                            <Center mt={'lg'}>
                                <Image w={206} h={90} src={appmedLogo} />
                            </Center>
                            <Title>{t('formRegister.invalidTokenTitle')}</Title>
                            <Paper maw={600} mt={'lg'} p={'xl'}>
                                <Stack gap={'md'}>
                                    <Text>{t('formRegister.invalidTokenMessage1')}</Text>
                                    <Text>{t('formRegister.invalidTokenMessage2')}</Text>
                                    <Text>{t('formRegister.invalidTokenMessage3')}</Text>
                                    <Text>{t('formRegister.invalidTokenMessage4')}</Text>
                                    <Text c="dimmed" size="sm" ta="center" mt={'md'}>
                                        {t('formRegister.redirectingToLogin', { seconds: countdown })}
                                    </Text>
                                    <Button fullWidth onClick={goToLogin} mt={'xs'}>
                                        {t('formRegister.goToLogin')}
                                    </Button>
                                </Stack>
                            </Paper>
                        </Stack>
                    </Container>
                </Shell>
            );
        }

        return (
            <Shell>
                <Container mt={0} mb={0} size={'default'} my={40} bg={'var(--mantine-color-gray-1)'} mih={'100vh'}>
                    <Stack align={'center'} w={'100%'} h={'auto'} gap={'sm'} justify={'flex-start'}>
                        <Center mt={'lg'}>
                            <Image w={206} h={90} src={appmedLogo} />
                        </Center>
                        <Title>{t('formGlobal.welcome')}</Title>
                        <Text c="dimmed" size="sm">
                            {t('formRegister.createYourAccount')}
                        </Text>
                        <Paper maw={478} mt={'lg'}>
                            <form
                                onSubmit={form.onSubmit(
                                    (values) => {
                                        handleSubmit(values);
                                    },
                                    (errors) => {
                                        PostHogService.capture('registration form validation failed', { fields: Object.keys(errors) });
                                        setError(t('formRegister.pleaseFixErrors'));
                                    }
                                )}
                            >
                                <Stack w={'100%'} gap={'md'}>
                                    <TextInput
                                        label={
                                            isAnonymizedMode
                                                ? t('formInvitation.participantNumber') || 'Participant Number'
                                                : t('formGlobal.firstName')
                                        }
                                        placeholder={
                                            isAnonymizedMode
                                                ? t('formInvitation.participantNumberPlaceholder') || 'e.g., P-0123'
                                                : t('formGlobal.enterFirstName')
                                        }
                                        {...form.getInputProps('firstName')}
                                        disabled={isAnonymizedMode}
                                        withAsterisk
                                    />
                                    <TextInput
                                        label={
                                            isAnonymizedMode
                                                ? t('formInvitation.participantInitial') || 'Participant Initial'
                                                : t('formGlobal.lastName')
                                        }
                                        placeholder={
                                            isAnonymizedMode
                                                ? t('formInvitation.participantInitialPlaceholder') || 'e.g., AB'
                                                : t('formGlobal.enterLastName')
                                        }
                                        {...form.getInputProps('lastName')}
                                        disabled={isAnonymizedMode}
                                        withAsterisk
                                    />
                                    <TextInput
                                        disabled
                                        label={t('formGlobal.email')}
                                        placeholder={t('formGlobal.enterEmailAddress')}
                                        leftSection={<IconAt size={16} />}
                                        {...form.getInputProps('email')}
                                        withAsterisk
                                    />
                                    <PasswordInput
                                        label={t('formGlobal.password')}
                                        placeholder={t('formGlobal.enterPassword')}
                                        leftSection={<IconLock size={16} />}
                                        {...form.getInputProps('password')}
                                        withAsterisk
                                    />
                                    <PasswordInput
                                        label={t('formGlobal.confirmPassword')}
                                        placeholder={t('formGlobal.confirmPassword')}
                                        leftSection={<IconLock size={16} />}
                                        {...form.getInputProps('confirmPassword')}
                                        withAsterisk
                                    />
                                    <Text size={'sm'} c={'dimmed'} mt={'xs'}>
                                        {t('formRegister.reviewAppMed')}
                                        <Anchor onClick={openTerms}>{t('formRegister.termsOfUse')}</Anchor>
                                        {t('formRegister.and')}
                                        <Anchor onClick={openPrivacy}>{t('formRegister.privacyPolicy')}</Anchor>
                                    </Text>
                                    <Button fullWidth type="submit" loading={isSubmitting} mt={'xs'}>
                                        {t('formRegister.createAccount')}
                                    </Button>
                                    <Anchor href="/login" size="sm" style={{ textAlign: 'center' }}>
                                        {t('formRegister.alreadyHaveAnAccount')}
                                    </Anchor>
                                </Stack>
                            </form>
                            {error && (
                                <Notification color="red" onClose={() => setError(null)}>
                                    {error}
                                </Notification>
                            )}
                        </Paper>
                    </Stack>
                    <TermsModal onClose={closeTerms} opened={openedTerms} />
                    <PrivacyModal onClose={closePrivacy} opened={openedPrivacy} />
                </Container>
            </Shell>
        );
    })
);

export default RegisterForm;
